import React, { useState } from "react";
import { Building2, Calendar, ChevronLeft, ChevronRight, MapPin } from "lucide-react";
import { TspCard } from "../../../components/primitives";
import { formatDate, formatDateRange } from "../../../utils/tspFormat";

const mapsUrl = (query) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
const PAGE_SIZE = 9;

function tag(item) {
  return item.is_free ? "Free" : `₹${item.charges}`;
}

function LogisticsColumn({ icon: Icon, title, items = [], buildDetail, permitted, subject }) {
  const [page, setPage] = useState(1);
  const totalItems = (items || []).length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const pagedItems = (items || []).slice(startIndex, startIndex + PAGE_SIZE);

  return (
    <div className="tsp-logistics-column">
      <h4 className="tsp-logistics-heading">
        <Icon size={18} /> {title}
      </h4>
      {!permitted ? (
        <p className="tsp-muted-text">You do not have permission to view {title.toLowerCase()}.</p>
      ) : items.length === 0 ? (
        <p className="tsp-muted-text">No {title.toLowerCase()} recorded on {subject} yet.</p>
      ) : (
        <>
          <ul className="tsp-logistics-cards-grid">
            {pagedItems.map((item) => {
              const locationText = item.location || [item.city_name, item.state_name].filter(Boolean).join(", ") || "—";
              const addressText = item.address || "—";
              const dateText = item.journey_date
                ? formatDate(item.journey_date)
                : item.start_date
                ? (item.end_date && item.end_date !== item.start_date ? formatDateRange(item.start_date, item.end_date) : formatDate(item.start_date))
                : item.date
                ? formatDate(item.date)
                : "—";

              return (
                <li key={item.id} className="tsp-logistics-item">
                  <div className="tsp-logistics-item-header">
                    <span className="tsp-logistics-name">{item.name}</span>
                    <span className={`tsp-status-pill ${item.is_free ? "tsp-status-pill-success" : "tsp-status-pill-danger"}`}>{tag(item)}</span>
                  </div>

                  <div className="tsp-logistics-meta-list">
                    <div className="tsp-logistics-meta-row">
                      <MapPin size={13} className="tsp-logistics-meta-icon" aria-hidden="true" />
                      <span className="tsp-logistics-meta-text">
                        <strong className="tsp-logistics-meta-label">Location:</strong>
                        <span>{locationText}</span>
                      </span>
                    </div>

                    <div className="tsp-logistics-meta-row">
                      <Building2 size={13} className="tsp-logistics-meta-icon" aria-hidden="true" />
                      <span className="tsp-logistics-meta-text">
                        <strong className="tsp-logistics-meta-label">Address:</strong>
                        <span>{addressText}</span>
                      </span>
                    </div>

                    <div className="tsp-logistics-meta-row">
                      <Calendar size={13} className="tsp-logistics-meta-icon" aria-hidden="true" />
                      <span className="tsp-logistics-meta-text">
                        <strong className="tsp-logistics-meta-label">Date of Journey:</strong>
                        <span>{dateText}</span>
                      </span>
                    </div>
                  </div>

                  <div className="tsp-logistics-detail-row">
                    <span>{buildDetail(item)}</span>
                    <a
                      href={mapsUrl([item.name, item.address && item.address !== "—" ? item.address : null, item.city_name, item.state_name].filter(Boolean).join(", "))}
                      target="_blank"
                      rel="noreferrer"
                      className="tsp-map-link"
                    >
                      View in Map
                    </a>
                  </div>
                </li>
              );
            })}
          </ul>

          {totalPages > 1 && (
            <div className="tsp-logistics-pagination">
              <span className="tsp-logistics-pagination-info">
                Showing {startIndex + 1}–{Math.min(startIndex + PAGE_SIZE, totalItems)} of {totalItems}
              </span>
              <div className="tsp-logistics-pagination-actions">
                <button
                  type="button"
                  className="tsp-btn tsp-btn-outline tsp-btn-sm"
                  disabled={currentPage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  aria-label="Previous page"
                >
                  <ChevronLeft size={16} aria-hidden="true" /> Previous
                </button>
                <span className="tsp-logistics-page-indicator">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  className="tsp-btn tsp-btn-outline tsp-btn-sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  aria-label="Next page"
                >
                  Next <ChevronRight size={16} aria-hidden="true" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const accommodationDetail = (item) => `${item.capacity ?? "—"} persons${item.remarks ? ` · ${item.remarks}` : ""}`;

// `logistics` is GET /api/tsp/stats#logistics — accommodation
// is `null` when the caller lacks that record type's own view
// permission, and `[]` (not null) when it is simply empty.
export default function LogisticsSummarySection({ logistics, subject = "this journey" }) {
  return (
    <TspCard title="Logistics Summary" className="tsp-title-18">
      <div className="tsp-logistics-wrapper">
        <LogisticsColumn icon={Building2} title="Accommodation" items={logistics?.accommodation || []} permitted={logistics?.accommodation !== null} buildDetail={accommodationDetail} subject={subject} />
      </div>
    </TspCard>
  );
}
